export const PERMISSION_CATEGORIES = {
  USERS: "users",
  BROADCASTS: "broadcasts",
  ANALYTICS: "analytics",
  SETTINGS: "settings",
  KNOWLEDGE: "knowledge",
  SUPPORT: "support",
  CRM: "crm",
  MANAGERS: "managers",
  RBAC: "rbac",
  ADMIN_USERS: "admin-users",
  API_KEYS: "api-keys",
  MCP: "mcp",
  CHAT: "chat",
  AI: "ai",
  COURSES: "courses",
  CONTENT: "content",
  SUBSCRIPTIONS: "subscriptions",
  NOTIFICATIONS: "notifications",
  TRIGGERS: "triggers",
  // Коммерция
  PRODUCTS: "products",
  CHANNEL_PASSES: "channel-passes",
  ORDERS: "orders",
  ORDER_STATUSES: "order-statuses",
  PAYMENTS: "payments",
  // Аудит
  AUDIT_LOGS: "audit-logs",
  // Telegram клиент
  TELEGRAM_CLIENT: "telegram-client",
  // Модерация
  MODERATION: "moderation",
  REPORTS: "reports",
  // Приглашения пользователей
  USER_INVITATIONS: "user-invitations",
  INVITATION_TEMPLATES: "invitation-templates",
  // Telegram боты
  TELEGRAM_BOTS: "telegram-bots",
  // Модерация чатов и игр
  CHAT_MODERATION: "chat-moderation",
  GAME_MODERATION: "game-moderation",
  VIOLATIONS: "violations",
  CURRENCIES: "currencies",
  STAFF_ANALYTICS: "staff-analytics",
  BOOKS: "books",
  LIVESTREAMS: "livestreams",
  // Теги и UTM
  TAGS: "tags",
  UTM_SOURCES: "utm-sources",
  COUNTRIES: "countries",
  // Промокоды
  PROMO_CODES: "promo-codes",
  // Аудитория / сегментация
  AUDIENCE: "audience",
  // Шаблоны писем и сообщений
  EMAIL_TEMPLATES: "email-templates",
  MESSAGE_TEMPLATES: "message-templates",
  // Аутентификация и self-service
  AUTH: "auth",
  ACCESS: "access",
  ME: "me",
  // Социальная сеть
  SOCIAL: "social",
  SOCIAL_MODERATION: "social-moderation",
  // Звонки и игры (плеер)
  CALLS: "calls",
  GAMES: "games",
  // Контент и привязки
  CONTENT_TAGS: "content-tags",
  // Инфраструктура и интеграции
  CONFIG: "config",
  INTEGRATIONS: "integrations",
  TELEGRAM_CHANNELS: "telegram-channels",
  TELEGRAM: "telegram",
  MATRIX: "matrix",
  FILES: "files",
  LEGAL: "legal",
  LOCALIZATION: "localization",
  TELEMETRY: "telemetry",
  PRESENCE: "presence",
  DASHBOARD: "dashboard",
  SEARCH: "search",
  ERROR_REPORTS: "error-reports",
  // Подписки и продукты — расширения
  SUBSCRIPTION_PLANS: "subscription-plans",
  FAMILY_SUBSCRIPTIONS: "family-subscriptions",
  CHANNEL_MEMBERSHIPS: "channel-memberships",
  PRICING_TIERS: "pricing-tiers",
  USER_BONUSES: "user-bonuses",
  // Клуб и встречи
  CLUB: "club",
  DAILY_PLANNER: "daily-planner",
  // Прочее
  CHAT_ANALYSIS: "chat-analysis",
  RECOMMENDATIONS: "recommendations",
  REPORTING: "reporting",
  TRACKS: "tracks",
  BREAKOUT_ROOMS: "breakout-rooms",
  LEADS: "leads",
} as const;

export type PermissionCategory =
  typeof PERMISSION_CATEGORIES[keyof typeof PERMISSION_CATEGORIES];

export type PermissionDefinition = {
  code: string;
  name: string;
  description?: string;
  category: PermissionCategory;
  /**
   * Зависимости — права, которые автоматически даются вместе с этим правом.
   * Используется для UI-подсказок и runtime-расчёта effective permissions.
   */
  implies?: string[];
};

export const PERMISSION_CATEGORY_LABELS: Record<PermissionCategory, string> = {
  users: "Пользователи",
  broadcasts: "Рассылки",
  analytics: "Аналитика",
  settings: "Настройки",
  knowledge: "База знаний",
  support: "Поддержка",
  crm: "CRM",
  managers: "Менеджеры",
  rbac: "Роли и права",
  "admin-users": "Администраторы",
  "api-keys": "API ключи",
  mcp: "MCP (ИИ-клиенты)",
  chat: "Чаты",
  ai: "ИИ",
  courses: "Курсы",
  content: "Контент",
  subscriptions: "Подписки",
  notifications: "Уведомления",
  triggers: "Триггеры",
  products: "Товары",
  "channel-passes": "Доступы в каналы",
  orders: "Заказы",
  "order-statuses": "Статусы заказов",
  payments: "Платежи",
  "audit-logs": "Аудит-логи",
  "telegram-client": "Telegram чаты",
  moderation: "Модерация контента",
  reports: "Жалобы",
  "user-invitations": "Приглашения пользователей",
  "invitation-templates": "Шаблоны приглашений",
  "telegram-bots": "Telegram боты",
  "chat-moderation": "Модерация чатов",
  "game-moderation": "Модерация игр",
  violations: "Нарушения",
  currencies: "Валюты",
  "staff-analytics": "Аналитика сотрудников",
  books: "Библиотека (книги)",
  livestreams: "Прямые трансляции",
  tags: "Теги",
  "utm-sources": "UTM источники",
  countries: "Страны",
  "promo-codes": "Промокоды",
  audience: "Аудитория / сегменты",
  "email-templates": "Шаблоны писем",
  "message-templates": "Шаблоны сообщений",
  auth: "Аутентификация",
  access: "Доступы пользователя",
  me: "Личный кабинет",
  social: "Социальная сеть",
  "social-moderation": "Модерация соцсети",
  calls: "Звонки",
  games: "Игры",
  "content-tags": "Теги контента",
  config: "Конфигурация",
  integrations: "Интеграции",
  "telegram-channels": "Telegram каналы",
  telegram: "Telegram (общее)",
  matrix: "Matrix / E2EE",
  files: "Файлы",
  legal: "Юридические страницы",
  localization: "Локализация",
  telemetry: "Телеметрия",
  presence: "Присутствие",
  dashboard: "Дашборд",
  search: "Поиск",
  "error-reports": "Отчёты об ошибках",
  "subscription-plans": "Абонементы",
  "family-subscriptions": "Семейные подписки",
  "channel-memberships": "Членство в каналах",
  "pricing-tiers": "Тарифы",
  "user-bonuses": "Бонусы пользователей",
  club: "Клуб",
  "daily-planner": "Ежедневный планировщик",
  "chat-analysis": "Анализ чатов",
  recommendations: "Рекомендации",
  reporting: "Отчётность",
  tracks: "Треки",
  "breakout-rooms": "Breakout-залы",
  leads: "Лиды и воронки",
};

export const SYSTEM_PERMISSIONS = {
  // Users
  USERS_READ: "users.read",
  USERS_CREATE: "users.create",
  USERS_UPDATE: "users.update",
  USERS_DELETE: "users.delete",
  // Auth
  AUTH_READ: "auth.read",
  AUTH_WRITE: "auth.write",
  AUTH_RESET: "auth.reset",
  AUTH_SEND: "auth.send",
  AUTH_VERIFY: "auth.verify",
  // Me / Access
  ME_READ: "me.read",
  ME_WRITE: "me.write",
  ME_RESET: "me.reset",
  ME_UNLOCK: "me.unlock",
  ACCESS_READ: "access.read",
  // Broadcasts
  BROADCASTS_READ: "broadcasts.read",
  BROADCASTS_CREATE: "broadcasts.create",
  BROADCASTS_UPDATE: "broadcasts.update",
  BROADCASTS_DELETE: "broadcasts.delete",
  BROADCASTS_SEND: "broadcasts.send",
  BROADCASTS_CANCEL: "broadcasts.cancel",
  BROADCASTS_PREVIEW: "broadcasts.preview",
  BROADCASTS_MANAGE: "broadcasts.manage",
  // Analytics
  ANALYTICS_VIEW: "analytics.view",
  ANALYTICS_MANAGE: "analytics.manage",
  ANALYTICS_EXPORT: "analytics.export",
  // Settings / Config
  SETTINGS_READ: "settings.read",
  SETTINGS_WRITE: "settings.write",
  CONFIG_READ: "config.read",
  CONFIG_WRITE: "config.write",
  // Knowledge
  KNOWLEDGE_READ: "knowledge.read",
  KNOWLEDGE_WRITE: "knowledge.write",
  KNOWLEDGE_DELETE: "knowledge.delete",
  // Support
  SUPPORT_READ: "support.read",
  SUPPORT_WRITE: "support.write",
  SUPPORT_RESPOND: "support.respond",
  SUPPORT_CLOSE: "support.close",
  // CRM / Leads
  CRM_READ: "crm.read",
  CRM_WRITE: "crm.write",
  CRM_SYNC: "crm.sync",
  CRM_MANAGE: "crm.manage",
  CRM_LEADS_READ: "crm.leads.read",
  CRM_LEADS_MANAGE: "crm.leads.manage",
  LEADS_READ: "leads.read",
  LEADS_WRITE: "leads.write",
  // Managers
  MANAGERS_READ: "managers.read",
  MANAGERS_MANAGE: "managers.manage",
  // RBAC
  RBAC_ROLES_READ: "rbac.roles.read",
  RBAC_ROLES_CREATE: "rbac.roles.create",
  RBAC_ROLES_UPDATE: "rbac.roles.update",
  RBAC_ROLES_DELETE: "rbac.roles.delete",
  RBAC_ROLES_MANAGE: "rbac.roles.manage",
  RBAC_PERMISSIONS_READ: "rbac.permissions.read",
  RBAC_PERMISSIONS_CREATE: "rbac.permissions.create",
  RBAC_PERMISSIONS_UPDATE: "rbac.permissions.update",
  RBAC_PERMISSIONS_DELETE: "rbac.permissions.delete",
  RBAC_PERMISSIONS_MANAGE: "rbac.permissions.manage",
  RBAC_USERS_READ: "rbac.users.read",
  RBAC_USERS_ASSIGN: "rbac.users.assign",
  RBAC_SYNC: "rbac.sync",
  // Admin users
  ADMIN_USERS_READ: "admin-users.read",
  ADMIN_USERS_CREATE: "admin-users.create",
  ADMIN_USERS_UPDATE: "admin-users.update",
  ADMIN_USERS_DELETE: "admin-users.delete",
  // API keys
  API_KEYS_READ: "api-keys.read",
  API_KEYS_WRITE: "api-keys.write",
  // MCP
  MCP_USE: "mcp.use",
  MCP_MANAGE: "mcp.manage",
  // AI
  AI_READ: "ai.read",
  AI_WRITE: "ai.write",
  AI_ARCHIVE: "ai.archive",
  AI_ASSISTANT_USE: "ai.assistant.use",
  AI_SANDBOX: "ai.sandbox",
  // Courses
  COURSES_READ: "courses.read",
  COURSES_CREATE: "courses.create",
  COURSES_UPDATE: "courses.update",
  COURSES_DELETE: "courses.delete",
  COURSES_PUBLISH: "courses.publish",
  COURSES_CANCEL: "courses.cancel",
  COURSES_RESTORE: "courses.restore",
  // Books
  BOOKS_READ: "books.read",
  BOOKS_CREATE: "books.create",
  BOOKS_UPDATE: "books.update",
  BOOKS_DELETE: "books.delete",
  BOOKS_PUBLISH: "books.publish",
  // Content
  CONTENT_READ: "content.read",
  CONTENT_WRITE: "content.write",
  CONTENT_TAGS_READ: "content-tags.read",
  CONTENT_TAGS_WRITE: "content-tags.write",
  // Subscriptions
  SUBSCRIPTIONS_READ: "subscriptions.read",
  SUBSCRIPTIONS_CREATE: "subscriptions.create",
  SUBSCRIPTIONS_UPDATE: "subscriptions.update",
  SUBSCRIPTIONS_DELETE: "subscriptions.delete",
  SUBSCRIPTIONS_CANCEL: "subscriptions.cancel",
  SUBSCRIPTION_PLANS_READ: "subscription-plans.read",
  SUBSCRIPTION_PLANS_CREATE: "subscription-plans.create",
  SUBSCRIPTION_PLANS_UPDATE: "subscription-plans.update",
  SUBSCRIPTION_PLANS_DELETE: "subscription-plans.delete",
  SUBSCRIPTION_PLANS_RESTORE: "subscription-plans.restore",
  FAMILY_SUBSCRIPTIONS_READ: "family-subscriptions.read",
  FAMILY_SUBSCRIPTIONS_WRITE: "family-subscriptions.write",
  CHANNEL_MEMBERSHIPS_READ: "channel-memberships.read",
  CHANNEL_MEMBERSHIPS_WRITE: "channel-memberships.write",
  CHANNEL_MEMBERSHIPS_SEND: "channel-memberships.send",
  CHANNEL_MEMBERSHIPS_RESEND: "channel-memberships.resend",
  CHANNEL_MEMBERSHIPS_SYNC: "channel-memberships.sync",
  CHANNEL_MEMBERSHIPS_EXPORT: "channel-memberships.export",
  PRICING_TIERS_READ: "pricing-tiers.read",
  PRICING_TIERS_WRITE: "pricing-tiers.write",
  USER_BONUSES_READ: "user-bonuses.read",
  USER_BONUSES_WRITE: "user-bonuses.write",
  // Notifications
  NOTIFICATIONS_READ: "notifications.read",
  NOTIFICATIONS_WRITE: "notifications.write",
  // Triggers
  TRIGGERS_READ: "triggers.read",
  TRIGGERS_WRITE: "triggers.write",
  // Products
  PRODUCTS_READ: "products.read",
  PRODUCTS_WRITE: "products.write",
  // Channel passes
  CHANNEL_PASSES_READ: "channel-passes.read",
  CHANNEL_PASSES_CREATE: "channel-passes.create",
  CHANNEL_PASSES_UPDATE: "channel-passes.update",
  CHANNEL_PASSES_DELETE: "channel-passes.delete",
  CHANNEL_PASSES_RESTORE: "channel-passes.restore",
  // Orders
  ORDERS_READ: "orders.read",
  ORDERS_CREATE: "orders.create",
  ORDERS_UPDATE: "orders.update",
  ORDERS_DELETE: "orders.delete",
  ORDERS_RESTORE: "orders.restore",
  ORDERS_ASSIGN: "orders.assign",
  ORDERS_STATUS_CHANGE: "orders.status.change",
  ORDERS_NOTES_READ: "orders.notes.read",
  ORDERS_NOTES_WRITE: "orders.notes.write",
  ORDERS_HISTORY_READ: "orders.history.read",
  ORDER_STATUSES_READ: "order-statuses.read",
  ORDER_STATUSES_WRITE: "order-statuses.write",
  // Payments
  PAYMENTS_READ: "payments.read",
  PAYMENTS_CREATE: "payments.create",
  PAYMENTS_UPDATE: "payments.update",
  PAYMENTS_DELETE: "payments.delete",
  PAYMENTS_REFUND: "payments.refund",
  PAYMENTS_CANCEL: "payments.cancel",
  PAYMENTS_RESUME: "payments.resume",
  PAYMENTS_SYNC: "payments.sync",
  // Audit logs
  AUDIT_LOGS_READ: "audit-logs.read",
  // Telegram client
  TELEGRAM_CLIENT_READ: "telegram-client.read",
  TELEGRAM_CLIENT_WRITE: "telegram-client.write",
  TELEGRAM_CLIENT_PUBLIC: "telegram-client.public",
  TELEGRAM_CLIENT_SEND: "telegram-client.send",
  TELEGRAM_CLIENT_RESEND: "telegram-client.resend",
  TELEGRAM_CLIENT_CANCEL: "telegram-client.cancel",
  TELEGRAM_CLIENT_VERIFY: "telegram-client.verify",
  TELEGRAM_READ: "telegram.read",
  TELEGRAM_WRITE: "telegram.write",
  TELEGRAM_CHANNELS_READ: "telegram-channels.read",
  TELEGRAM_CHANNELS_WRITE: "telegram-channels.write",
  TELEGRAM_BOTS_READ: "telegram-bots.read",
  TELEGRAM_BOTS_WRITE: "telegram-bots.write",
  // Moderation
  MODERATION_READ: "moderation.read",
  MODERATION_WRITE: "moderation.write",
  REPORTS_READ: "reports.read",
  REPORTS_WRITE: "reports.write",
  // User invitations
  USER_INVITATIONS_READ: "user-invitations.read",
  USER_INVITATIONS_WRITE: "user-invitations.write",
  USER_INVITATIONS_SEND: "user-invitations.send",
  INVITATION_TEMPLATES_READ: "invitation-templates.read",
  INVITATION_TEMPLATES_WRITE: "invitation-templates.write",
  // Chat moderation
  CHAT_READ: "chat.read",
  CHAT_WRITE: "chat.write",
  CHAT_WARN: "chat.warn",
  CHAT_MUTE: "chat.mute",
  CHAT_UNMUTE: "chat.unmute",
  CHAT_KICK: "chat.kick",
  CHAT_BAN: "chat.ban",
  CHAT_ANALYZE: "chat.analyze",
  CHAT_MODERATION_READ: "chat-moderation.read",
  CHAT_MODERATION_WRITE: "chat-moderation.write",
  CHAT_ANALYSIS_READ: "chat-analysis.read",
  CHAT_ANALYSIS_WRITE: "chat-analysis.write",
  // Games
  GAMES_READ: "games.read",
  GAMES_WRITE: "games.write",
  GAMES_KICK: "games.kick",
  GAMES_BAN: "games.ban",
  GAMES_CANCEL: "games.cancel",
  GAME_MODERATION_READ: "game-moderation.read",
  GAME_MODERATION_WRITE: "game-moderation.write",
  // Violations
  VIOLATIONS_READ: "violations.read",
  VIOLATIONS_WRITE: "violations.write",
  VIOLATIONS_WARN: "violations.warn",
  VIOLATIONS_MUTE: "violations.mute",
  VIOLATIONS_UNMUTE: "violations.unmute",
  VIOLATIONS_BAN: "violations.ban",
  // Currencies
  CURRENCIES_READ: "currencies.read",
  CURRENCIES_WRITE: "currencies.write",
  // Staff analytics
  STAFF_ANALYTICS_READ: "staff-analytics.read",
  STAFF_ANALYTICS_WRITE: "staff-analytics.write",
  STAFF_ANALYTICS_EXPORT: "staff-analytics.export",
  // Livestreams
  LIVESTREAMS_READ: "livestreams.read",
  LIVESTREAMS_CREATE: "livestreams.create",
  LIVESTREAMS_UPDATE: "livestreams.update",
  LIVESTREAMS_DELETE: "livestreams.delete",
  LIVESTREAMS_PUBLISH: "livestreams.publish",
  LIVESTREAMS_UNPUBLISH: "livestreams.unpublish",
  LIVESTREAMS_CANCEL: "livestreams.cancel",
  LIVESTREAMS_PREVIEW: "livestreams.preview",
  LIVESTREAM_HOST: "livestream.host",
  LIVESTREAM_MANAGE: "livestream.manage",
  LIVESTREAM_ANALYTICS: "livestream.analytics",
  BREAKOUT_ROOMS_READ: "breakout-rooms.read",
  BREAKOUT_ROOMS_WRITE: "breakout-rooms.write",
  BREAKOUT_ROOMS_PREVIEW: "breakout-rooms.preview",
  // Tags
  TAGS_READ: "tags.read",
  TAGS_WRITE: "tags.write",
  TAGS_DELETE: "tags.delete",
  TAGS_ASSIGN: "tags.assign",
  TAGS_ARCHIVE: "tags.archive",
  TAGS_RESTORE: "tags.restore",
  // UTM
  UTM_SOURCES_READ: "utm-sources.read",
  UTM_SOURCES_WRITE: "utm-sources.write",
  COUNTRIES_READ: "countries.read",
  COUNTRIES_WRITE: "countries.write",
  // Promo codes
  PROMO_CODES_READ: "promo-codes.read",
  PROMO_CODES_CREATE: "promo-codes.create",
  PROMO_CODES_UPDATE: "promo-codes.update",
  PROMO_CODES_DELETE: "promo-codes.delete",
  // Audience
  AUDIENCE_READ: "audience.read",
  AUDIENCE_WRITE: "audience.write",
  AUDIENCE_PREVIEW: "audience.preview",
  // Templates
  EMAIL_TEMPLATES_MANAGE: "email-templates.manage",
  MESSAGE_TEMPLATES_READ: "message-templates.read",
  MESSAGE_TEMPLATES_WRITE: "message-templates.write",
  MESSAGE_TEMPLATES_PREVIEW: "message-templates.preview",
  // Social
  SOCIAL_FEED_READ: "social-feed.read",
  SOCIAL_POSTS_READ: "social-posts.read",
  SOCIAL_POSTS_WRITE: "social-posts.write",
  SOCIAL_POSTS_PUBLISH: "social-posts.publish",
  SOCIAL_COMMENTS_READ: "social-comments.read",
  SOCIAL_COMMENTS_WRITE: "social-comments.write",
  SOCIAL_LESSON_COMMENTS_READ: "social-lesson-comments.read",
  SOCIAL_LESSON_COMMENTS_WRITE: "social-lesson-comments.write",
  SOCIAL_LIKES_WRITE: "social-likes.write",
  SOCIAL_FOLLOWS_READ: "social-follows.read",
  SOCIAL_FOLLOWS_WRITE: "social-follows.write",
  SOCIAL_PROFILES_READ: "social-profiles.read",
  SOCIAL_PROFILES_WRITE: "social-profiles.write",
  SOCIAL_REPORTS_READ: "social-reports.read",
  SOCIAL_REPORTS_WRITE: "social-reports.write",
  SOCIAL_MODERATION_READ: "social-moderation.read",
  SOCIAL_MODERATION_APPROVE: "social-moderation.approve",
  SOCIAL_MODERATION_REJECT: "social-moderation.reject",
  SOCIAL_ADMIN_READ: "social-admin.read",
  SOCIAL_ADMIN_WRITE: "social-admin.write",
  // Calls
  CALLS_READ: "calls.read",
  CALLS_WRITE: "calls.write",
  // Files
  FILES_READ: "files.read",
  FILES_WRITE: "files.write",
  // Legal
  LEGAL_PAGES_READ: "legal-pages.read",
  LEGAL_PAGES_WRITE: "legal-pages.write",
  // Localization
  LOCALIZATION_READ: "localization.read",
  LOCALIZATION_WRITE: "localization.write",
  LOCALIZATION_EXPORT: "localization.export",
  LOCALIZATION_SYNC: "localization.sync",
  // Matrix
  MATRIX_KEYS_WRITE: "matrix-keys.write",
  MATRIX_ROOM_KEYS_READ: "matrix-room-keys.read",
  MATRIX_ROOM_KEYS_WRITE: "matrix-room-keys.write",
  MATRIX_TO_DEVICE_WRITE: "matrix-to-device.write",
  // Integrations / Webhooks
  INTEGRATIONS_READ: "integrations.read",
  INTEGRATIONS_WRITE: "integrations.write",
  // Misc
  PRESENCE_READ: "presence.read",
  PRESENCE_WRITE: "presence.write",
  DASHBOARD_READ: "dashboard.read",
  SEARCH_READ: "search.read",
  ERROR_REPORTS_WRITE: "error-reports.write",
  TELEMETRY_READ: "telemetry.read",
  TELEMETRY_WRITE: "telemetry.write",
  TRACKS_WRITE: "tracks.write",
  // Club / Planner
  CLUB_READ: "club.read",
  CLUB_WRITE: "club.write",
  DAILY_PLANNER_READ: "daily-planner.read",
  DAILY_PLANNER_WRITE: "daily-planner.write",
  DAILY_PLANNER_PUBLISH: "daily-planner.publish",
  // Recommendations / Reporting
  RECOMMENDATIONS_READ: "recommendations.read",
  RECOMMENDATIONS_WRITE: "recommendations.write",
  REPORTING_READ: "reporting.read",
  REPORTING_WRITE: "reporting.write",
} as const;

export type SystemPermission =
  typeof SYSTEM_PERMISSIONS[keyof typeof SYSTEM_PERMISSIONS];

/**
 * Маппинг устаревших permission codes на новые канонические.
 * Используется одноразовым скриптом миграции для обновления уже назначенных прав в ролях.
 * После миграции эти коды НЕ должны встречаться в БД и не входят в ALL_PERMISSIONS.
 */
export const LEGACY_PERMISSION_MAP: Record<string, string> = {
  "broadcast.read": "broadcasts.read",
  "broadcast.write": "broadcasts.manage",
  "rbac.roles.write": "rbac.roles.manage",
  "rbac.permissions.write": "rbac.permissions.manage",
  "knowledge-base.read": "knowledge.read",
  "knowledge-base.write": "knowledge.write",
  "analytics.read": "analytics.view",
  "analytics.write": "analytics.manage",
  // Старый colon-разделитель leads:delete заменён на canonical leads.write
  // (всё управление лидами под одним write-правом; точечный delete не выделяем).
  "leads:delete": "leads.write",
  "leads:read": "leads.read",
  "leads:write": "leads.write",
};

export const ALL_PERMISSIONS: PermissionDefinition[] = [
  // ==================== USERS ====================
  {
    code: "users.read",
    name: "Просмотр пользователей",
    description: "Просмотр списка пользователей и их профилей",
    category: "users",
  },
  {
    code: "users.create",
    name: "Создание пользователей",
    description: "Создание новых пользователей",
    category: "users",
    implies: ["users.read"],
  },
  {
    code: "users.update",
    name: "Редактирование пользователей",
    description: "Обновление данных пользователей",
    category: "users",
    implies: ["users.read"],
  },
  {
    code: "users.delete",
    name: "Удаление пользователей",
    description: "Удаление пользователей и связанных данных",
    category: "users",
    implies: ["users.read"],
  },

  // ==================== AUTH ====================
  {
    code: "auth.read",
    name: "Просмотр аутентификации",
    description: "Чтение состояния сессии, профиля /me и invite-токенов",
    category: "auth",
  },
  {
    code: "auth.write",
    name: "Аутентификация (запись)",
    description: "Логин, logout, refresh, change-password, set-password",
    category: "auth",
  },
  {
    code: "auth.reset",
    name: "Сброс пароля",
    description: "Reset-password / forgot-password flow",
    category: "auth",
  },
  {
    code: "auth.send",
    name: "Отправка кодов аутентификации",
    description: "Отправка OTP / verify-кодов на email/telegram",
    category: "auth",
  },
  {
    code: "auth.verify",
    name: "Проверка кодов аутентификации",
    description: "Подтверждение OTP / verify-кодов",
    category: "auth",
  },

  // ==================== ME / ACCESS ====================
  {
    code: "me.read",
    name: "Просмотр личного кабинета",
    description: "Чтение собственного профиля, настроек и связанных сущностей",
    category: "me",
  },
  {
    code: "me.write",
    name: "Изменение личного кабинета",
    description: "Обновление собственного профиля и настроек",
    category: "me",
    implies: ["me.read"],
  },
  {
    code: "me.reset",
    name: "Сброс настроек ЛК",
    description: "Сброс кодов / привязок в личном кабинете",
    category: "me",
  },
  {
    code: "me.unlock",
    name: "Разблокировка ЛК",
    description: "Снятие локов / ограничений в личном кабинете",
    category: "me",
  },
  {
    code: "access.read",
    name: "Просмотр доступов",
    description: "Чтение собственных доступов: курсы, каналы, бонусы",
    category: "access",
  },

  // ==================== BROADCASTS ====================
  {
    code: "broadcasts.read",
    name: "Просмотр рассылок",
    description: "Просмотр списка рассылок и их параметров",
    category: "broadcasts",
  },
  {
    code: "broadcasts.create",
    name: "Создание рассылок",
    description: "Создание новых рассылок",
    category: "broadcasts",
    implies: ["broadcasts.read", "audience.read"],
  },
  {
    code: "broadcasts.update",
    name: "Редактирование рассылок",
    description: "Изменение параметров рассылок",
    category: "broadcasts",
    implies: ["broadcasts.read"],
  },
  {
    code: "broadcasts.delete",
    name: "Удаление рассылок",
    description: "Удаление рассылок",
    category: "broadcasts",
    implies: ["broadcasts.read"],
  },
  {
    code: "broadcasts.send",
    name: "Отправка рассылок",
    description: "Запуск рассылок и отправка сообщений аудитории",
    category: "broadcasts",
    implies: ["broadcasts.read", "audience.read"],
  },
  {
    code: "broadcasts.cancel",
    name: "Отмена рассылок",
    description: "Отмена запущенных или запланированных рассылок",
    category: "broadcasts",
    implies: ["broadcasts.read"],
  },
  {
    code: "broadcasts.preview",
    name: "Предпросмотр рассылок",
    description: "Предпросмотр сообщений рассылки и её аудитории",
    category: "broadcasts",
    implies: ["broadcasts.read"],
  },
  {
    code: "broadcasts.manage",
    name: "Полное управление рассылками",
    description: "Создание, редактирование и удаление рассылок (агрегирующее право)",
    category: "broadcasts",
    implies: [
      "broadcasts.read",
      "broadcasts.create",
      "broadcasts.update",
      "broadcasts.delete",
      "broadcasts.send",
      "broadcasts.cancel",
      "broadcasts.preview",
    ],
  },

  // ==================== ANALYTICS ====================
  {
    code: "analytics.view",
    name: "Просмотр аналитики",
    description: "Доступ к метрикам, графикам и дашбордам",
    category: "analytics",
  },
  {
    code: "analytics.export",
    name: "Экспорт аналитики",
    description: "Выгрузка отчётов и данных в файлы",
    category: "analytics",
    implies: ["analytics.view"],
  },
  {
    code: "analytics.manage",
    name: "Управление аналитикой",
    description: "Настройка конфигурации и параметров аналитики",
    category: "analytics",
    implies: ["analytics.view"],
  },

  // ==================== SETTINGS / CONFIG ====================
  {
    code: "settings.read",
    name: "Просмотр настроек",
    description: "Просмотр текущих настроек системы",
    category: "settings",
  },
  {
    code: "settings.write",
    name: "Редактирование настроек",
    description: "Изменение параметров системы",
    category: "settings",
    implies: ["settings.read"],
  },
  {
    code: "config.read",
    name: "Просмотр конфигурации",
    description: "Чтение публичной и админской конфигурации платформы",
    category: "config",
  },
  {
    code: "config.write",
    name: "Управление конфигурацией",
    description: "Изменение конфигурации платформы",
    category: "config",
    implies: ["config.read"],
  },

  // ==================== KNOWLEDGE ====================
  {
    code: "knowledge.read",
    name: "Просмотр базы знаний",
    description: "Просмотр статей и документов базы знаний",
    category: "knowledge",
  },
  {
    code: "knowledge.write",
    name: "Управление базой знаний",
    description: "Создание и редактирование материалов базы знаний",
    category: "knowledge",
    implies: ["knowledge.read"],
  },
  {
    code: "knowledge.delete",
    name: "Удаление из базы знаний",
    description: "Удаление материалов базы знаний",
    category: "knowledge",
    implies: ["knowledge.read"],
  },

  // ==================== SUPPORT ====================
  {
    code: "support.read",
    name: "Просмотр тикетов",
    description: "Просмотр обращений пользователей",
    category: "support",
  },
  {
    code: "support.write",
    name: "Управление тикетами",
    description: "Создание и редактирование обращений поддержки",
    category: "support",
    implies: ["support.read"],
  },
  {
    code: "support.respond",
    name: "Ответ на тикеты",
    description: "Ответы пользователям в обращениях поддержки",
    category: "support",
    implies: ["support.read"],
  },
  {
    code: "support.close",
    name: "Закрытие тикетов",
    description: "Закрытие обращений поддержки",
    category: "support",
    implies: ["support.read"],
  },

  // ==================== CRM / LEADS ====================
  {
    code: "crm.read",
    name: "Просмотр CRM",
    description: "Просмотр данных CRM и сделок",
    category: "crm",
  },
  {
    code: "crm.write",
    name: "Управление CRM (запись)",
    description: "Создание и обновление сущностей CRM",
    category: "crm",
    implies: ["crm.read"],
  },
  {
    code: "crm.sync",
    name: "Синхронизация CRM",
    description: "Ручной запуск синхронизации с CRM",
    category: "crm",
    implies: ["crm.read"],
  },
  {
    code: "crm.manage",
    name: "Управление CRM",
    description: "Настройка интеграции с CRM",
    category: "crm",
    implies: ["crm.read", "crm.write"],
  },
  {
    code: "crm.leads.read",
    name: "Просмотр лидов",
    description: "Просмотр лидов, воронок и этапов CRM",
    category: "crm",
  },
  {
    code: "crm.leads.manage",
    name: "Управление лидами и воронками",
    description: "Настройка воронок, этапов и webhook лидов",
    category: "crm",
    implies: ["crm.leads.read"],
  },
  {
    code: "leads.read",
    name: "Просмотр лидов (домен)",
    description: "Просмотр лидов в нативном leads-домене",
    category: "leads",
  },
  {
    code: "leads.write",
    name: "Управление лидами (домен)",
    description: "Создание и обновление лидов в нативном leads-домене",
    category: "leads",
    implies: ["leads.read"],
  },

  // ==================== MANAGERS ====================
  {
    code: "managers.read",
    name: "Просмотр менеджеров",
    description: "Просмотр списка менеджеров и их статусов",
    category: "managers",
  },
  {
    code: "managers.manage",
    name: "Управление менеджерами",
    description: "Добавление и настройка менеджеров",
    category: "managers",
    implies: ["managers.read"],
  },

  // ==================== RBAC ====================
  {
    code: "rbac.roles.read",
    name: "Просмотр ролей",
    description: "Просмотр списка ролей и их настроек",
    category: "rbac",
  },
  {
    code: "rbac.roles.create",
    name: "Создание ролей",
    description: "Создание новых ролей",
    category: "rbac",
    implies: ["rbac.roles.read"],
  },
  {
    code: "rbac.roles.update",
    name: "Редактирование ролей",
    description: "Изменение существующих ролей",
    category: "rbac",
    implies: ["rbac.roles.read"],
  },
  {
    code: "rbac.roles.delete",
    name: "Удаление ролей",
    description: "Удаление ролей",
    category: "rbac",
    implies: ["rbac.roles.read"],
  },
  {
    code: "rbac.roles.manage",
    name: "Управление ролями",
    description: "Полное управление ролями (агрегирующее право)",
    category: "rbac",
    implies: [
      "rbac.roles.read",
      "rbac.roles.create",
      "rbac.roles.update",
      "rbac.roles.delete",
      "rbac.permissions.read",
    ],
  },
  {
    code: "rbac.permissions.read",
    name: "Просмотр прав",
    description: "Просмотр списка прав и их параметров",
    category: "rbac",
  },
  {
    code: "rbac.permissions.create",
    name: "Создание прав",
    description: "Создание новых прав в системе",
    category: "rbac",
    implies: ["rbac.permissions.read"],
  },
  {
    code: "rbac.permissions.update",
    name: "Редактирование прав",
    description: "Изменение существующих прав",
    category: "rbac",
    implies: ["rbac.permissions.read"],
  },
  {
    code: "rbac.permissions.delete",
    name: "Удаление прав",
    description: "Удаление прав из системы",
    category: "rbac",
    implies: ["rbac.permissions.read"],
  },
  {
    code: "rbac.permissions.manage",
    name: "Управление правами",
    description: "Назначение прав ролям и управление их составом",
    category: "rbac",
    implies: [
      "rbac.permissions.read",
      "rbac.permissions.create",
      "rbac.permissions.update",
      "rbac.permissions.delete",
    ],
  },
  {
    code: "rbac.users.read",
    name: "Просмотр пользователей (RBAC)",
    description: "Просмотр пользователей в контексте ролей",
    category: "rbac",
  },
  {
    code: "rbac.users.assign",
    name: "Назначение ролей",
    description: "Назначение и смена ролей пользователей",
    category: "rbac",
    implies: ["rbac.users.read", "rbac.roles.read"],
  },
  {
    code: "rbac.sync",
    name: "Синхронизация RBAC",
    description: "Запуск синхронизации каталога прав и ролей",
    category: "rbac",
    implies: ["rbac.roles.read", "rbac.permissions.read"],
  },

  // ==================== ADMIN USERS ====================
  {
    code: "admin-users.read",
    name: "Просмотр администраторов",
    description: "Просмотр списка администраторов и их ролей",
    category: "admin-users",
  },
  {
    code: "admin-users.create",
    name: "Создание администраторов",
    description: "Создание новых администраторов",
    category: "admin-users",
    implies: ["admin-users.read"],
  },
  {
    code: "admin-users.update",
    name: "Редактирование администраторов",
    description: "Изменение данных и ролей администраторов",
    category: "admin-users",
    implies: ["admin-users.read"],
  },
  {
    code: "admin-users.delete",
    name: "Удаление администраторов",
    description: "Удаление администраторов",
    category: "admin-users",
    implies: ["admin-users.read"],
  },

  // ==================== API KEYS ====================
  {
    code: "api-keys.read",
    name: "Просмотр API ключей",
    description: "Просмотр списка API ключей",
    category: "api-keys",
  },
  {
    code: "api-keys.write",
    name: "Управление API ключами",
    description: "Создание, изменение и удаление API ключей",
    category: "api-keys",
    implies: ["api-keys.read"],
  },

  // ==================== MCP ====================
  {
    code: "mcp.use",
    name: "Подключение ИИ-клиентов (MCP)",
    description: "Создание своих MCP-токенов и вход через OAuth; инструменты ограничены правами роли",
    category: "mcp",
  },
  {
    code: "mcp.manage",
    name: "Управление MCP-доступом",
    description: "Просмотр и отзыв MCP-токенов всех администраторов, журнал вызовов",
    category: "mcp",
    implies: ["mcp.use"],
  },

  // ==================== AI ====================
  {
    code: "ai.read",
    name: "Просмотр AI",
    description: "Просмотр AI настроек и логов",
    category: "ai",
  },
  {
    code: "ai.write",
    name: "Управление AI",
    description: "Управление AI настройками",
    category: "ai",
    implies: ["ai.read"],
  },
  {
    code: "ai.archive",
    name: "Архивирование AI",
    description: "Архивирование AI диалогов / истории",
    category: "ai",
    implies: ["ai.read"],
  },
  {
    code: "ai.sandbox",
    name: "Песочница ИИ",
    description: "Доступ к тестовым инструментам ИИ",
    category: "ai",
  },
  {
    code: "ai.assistant.use",
    name: "AI Ассистент",
    description: "Использование AI ассистента в админ-панели для управления сущностями, аналитики и переводов",
    category: "ai",
  },

  // ==================== COURSES ====================
  {
    code: "courses.read",
    name: "Просмотр курсов",
    description: "Просмотр списка курсов и модулей",
    category: "courses",
  },
  {
    code: "courses.create",
    name: "Создание курсов",
    description: "Создание новых курсов и модулей",
    category: "courses",
    implies: ["courses.read"],
  },
  {
    code: "courses.update",
    name: "Редактирование курсов",
    description: "Изменение курсов и модулей",
    category: "courses",
    implies: ["courses.read"],
  },
  {
    code: "courses.delete",
    name: "Удаление курсов",
    description: "Удаление курсов и модулей",
    category: "courses",
    implies: ["courses.read"],
  },
  {
    code: "courses.publish",
    name: "Публикация курсов",
    description: "Публикация и снятие с публикации курсов",
    category: "courses",
    implies: ["courses.read", "courses.update"],
  },
  {
    code: "courses.cancel",
    name: "Отмена курсов",
    description: "Отмена курсов / запусков",
    category: "courses",
    implies: ["courses.read"],
  },
  {
    code: "courses.restore",
    name: "Восстановление курсов",
    description: "Восстановление архивных курсов",
    category: "courses",
    implies: ["courses.read"],
  },

  // ==================== BOOKS ====================
  {
    code: "books.read",
    name: "Просмотр книг",
    description: "Просмотр списка книг и их метаданных",
    category: "books",
  },
  {
    code: "books.create",
    name: "Создание книг",
    description: "Создание новых книг",
    category: "books",
    implies: ["books.read"],
  },
  {
    code: "books.update",
    name: "Редактирование книг",
    description: "Обновление данных книг",
    category: "books",
    implies: ["books.read"],
  },
  {
    code: "books.delete",
    name: "Удаление книг",
    description: "Удаление книг и связанных материалов",
    category: "books",
    implies: ["books.read"],
  },
  {
    code: "books.publish",
    name: "Публикация книг",
    description: "Публикация и снятие с публикации книг",
    category: "books",
    implies: ["books.read", "books.update"],
  },

  // ==================== CONTENT ====================
  {
    code: "content.read",
    name: "Просмотр контента",
    description: "Просмотр контента платформы",
    category: "content",
  },
  {
    code: "content.write",
    name: "Управление контентом",
    description: "Создание и редактирование контента",
    category: "content",
    implies: ["content.read"],
  },
  {
    code: "content-tags.read",
    name: "Просмотр тегов контента",
    description: "Просмотр привязок тегов к сущностям контента",
    category: "content-tags",
  },
  {
    code: "content-tags.write",
    name: "Управление тегами контента",
    description: "Привязка/отвязка тегов к сущностям контента",
    category: "content-tags",
    implies: ["content-tags.read"],
  },

  // ==================== SUBSCRIPTIONS ====================
  {
    code: "subscriptions.read",
    name: "Просмотр подписок",
    description: "Просмотр списка подписок и статусов",
    category: "subscriptions",
  },
  {
    code: "subscriptions.create",
    name: "Создание подписок",
    description: "Создание новых подписок",
    category: "subscriptions",
    implies: ["subscriptions.read", "users.read"],
  },
  {
    code: "subscriptions.update",
    name: "Редактирование подписок",
    description: "Изменение подписок",
    category: "subscriptions",
    implies: ["subscriptions.read"],
  },
  {
    code: "subscriptions.delete",
    name: "Удаление подписок",
    description: "Удаление подписок",
    category: "subscriptions",
    implies: ["subscriptions.read"],
  },
  {
    code: "subscriptions.cancel",
    name: "Отмена подписок",
    description: "Отмена активных подписок",
    category: "subscriptions",
    implies: ["subscriptions.read", "payments.read"],
  },
  {
    code: "subscription-plans.read",
    name: "Просмотр абонементов",
    description: "Просмотр списка абонементов",
    category: "subscription-plans",
  },
  {
    code: "subscription-plans.create",
    name: "Создание абонементов",
    description: "Создание новых абонементов",
    category: "subscription-plans",
    implies: ["subscription-plans.read"],
  },
  {
    code: "subscription-plans.update",
    name: "Редактирование абонементов",
    description: "Обновление абонементов",
    category: "subscription-plans",
    implies: ["subscription-plans.read"],
  },
  {
    code: "subscription-plans.delete",
    name: "Удаление абонементов",
    description: "Удаление абонементов",
    category: "subscription-plans",
    implies: ["subscription-plans.read"],
  },
  {
    code: "subscription-plans.restore",
    name: "Восстановление абонементов",
    description: "Восстановление архивных абонементов",
    category: "subscription-plans",
    implies: ["subscription-plans.read"],
  },
  {
    code: "family-subscriptions.read",
    name: "Просмотр семейных подписок",
    description: "Просмотр семейных подписок и участников",
    category: "family-subscriptions",
  },
  {
    code: "family-subscriptions.write",
    name: "Управление семейными подписками",
    description: "Создание и редактирование семейных подписок",
    category: "family-subscriptions",
    implies: ["family-subscriptions.read"],
  },
  {
    code: "channel-memberships.read",
    name: "Просмотр членства в каналах",
    description: "Просмотр участников каналов",
    category: "channel-memberships",
  },
  {
    code: "channel-memberships.write",
    name: "Управление членством в каналах",
    description: "Управление участниками каналов",
    category: "channel-memberships",
    implies: ["channel-memberships.read"],
  },
  {
    code: "channel-memberships.send",
    name: "Выдача доступа к каналу",
    description: "Отправка инвайтов и выдача доступов в каналы",
    category: "channel-memberships",
    implies: ["channel-memberships.read"],
  },
  {
    code: "channel-memberships.resend",
    name: "Повторная отправка инвайтов",
    description: "Повторная отправка инвайтов в каналы",
    category: "channel-memberships",
    implies: ["channel-memberships.read"],
  },
  {
    code: "channel-memberships.sync",
    name: "Синхронизация членства",
    description: "Синхронизация участников каналов с Telegram",
    category: "channel-memberships",
    implies: ["channel-memberships.read"],
  },
  {
    code: "channel-memberships.export",
    name: "Экспорт членства",
    description: "Экспорт участников каналов",
    category: "channel-memberships",
    implies: ["channel-memberships.read"],
  },
  {
    code: "pricing-tiers.read",
    name: "Просмотр тарифов",
    description: "Просмотр тарифов абонементов",
    category: "pricing-tiers",
  },
  {
    code: "pricing-tiers.write",
    name: "Управление тарифами",
    description: "Создание и редактирование тарифов абонементов",
    category: "pricing-tiers",
    implies: ["pricing-tiers.read"],
  },
  {
    code: "user-bonuses.read",
    name: "Просмотр бонусов",
    description: "Просмотр выданных пользователям бонусов",
    category: "user-bonuses",
  },
  {
    code: "user-bonuses.write",
    name: "Управление бонусами",
    description: "Выдача и отзыв бонусов пользователей",
    category: "user-bonuses",
    implies: ["user-bonuses.read", "users.read"],
  },

  // ==================== NOTIFICATIONS / TRIGGERS ====================
  {
    code: "notifications.read",
    name: "Просмотр уведомлений",
    description: "Просмотр уведомлений и шаблонов",
    category: "notifications",
  },
  {
    code: "notifications.write",
    name: "Управление уведомлениями",
    description: "Создание и редактирование уведомлений",
    category: "notifications",
    implies: ["notifications.read"],
  },
  {
    code: "triggers.read",
    name: "Просмотр триггеров",
    description: "Просмотр списка триггеров",
    category: "triggers",
  },
  {
    code: "triggers.write",
    name: "Управление триггерами",
    description: "Создание и редактирование триггеров",
    category: "triggers",
    implies: ["triggers.read"],
  },

  // ==================== PRODUCTS / CHANNEL PASSES ====================
  {
    code: "products.read",
    name: "Просмотр товаров",
    description: "Просмотр списка товаров",
    category: "products",
  },
  {
    code: "products.write",
    name: "Управление товарами",
    description: "Создание и редактирование товаров",
    category: "products",
    implies: ["products.read"],
  },
  {
    code: "channel-passes.read",
    name: "Просмотр доступов в каналы",
    description: "Просмотр списка доступов в каналы",
    category: "channel-passes",
  },
  {
    code: "channel-passes.create",
    name: "Создание доступов в каналы",
    description: "Создание новых доступов в каналы",
    category: "channel-passes",
    implies: ["channel-passes.read"],
  },
  {
    code: "channel-passes.update",
    name: "Редактирование доступов в каналы",
    description: "Изменение доступов в каналы",
    category: "channel-passes",
    implies: ["channel-passes.read"],
  },
  {
    code: "channel-passes.delete",
    name: "Удаление доступов в каналы",
    description: "Удаление доступов в каналы",
    category: "channel-passes",
    implies: ["channel-passes.read"],
  },
  {
    code: "channel-passes.restore",
    name: "Восстановление доступов в каналы",
    description: "Восстановление архивных доступов в каналы",
    category: "channel-passes",
    implies: ["channel-passes.read"],
  },

  // ==================== ORDERS ====================
  {
    code: "orders.read",
    name: "Просмотр заказов",
    description: "Просмотр списка заказов",
    category: "orders",
  },
  {
    code: "orders.create",
    name: "Создание заказов",
    description: "Создание новых заказов",
    category: "orders",
    implies: ["orders.read"],
  },
  {
    code: "orders.update",
    name: "Редактирование заказов",
    description: "Изменение заказов",
    category: "orders",
    implies: ["orders.read"],
  },
  {
    code: "orders.delete",
    name: "Удаление заказов",
    description: "Удаление заказов",
    category: "orders",
    implies: ["orders.read"],
  },
  {
    code: "orders.restore",
    name: "Восстановление заказов",
    description: "Восстановление архивных заказов",
    category: "orders",
    implies: ["orders.read"],
  },
  {
    code: "orders.assign",
    name: "Назначение ответственного",
    description: "Назначение ответственного менеджера на заказ",
    category: "orders",
    implies: ["orders.read", "users.read"],
  },
  {
    code: "orders.status.change",
    name: "Изменение статуса заказа",
    description: "Изменение статуса заказов",
    category: "orders",
    implies: ["orders.read"],
  },
  {
    code: "orders.notes.read",
    name: "Просмотр заметок заказов",
    description: "Просмотр приватных заметок к заказам",
    category: "orders",
    implies: ["orders.read"],
  },
  {
    code: "orders.notes.write",
    name: "Добавление заметок",
    description: "Добавление и редактирование заметок к заказам",
    category: "orders",
    implies: ["orders.read", "orders.notes.read"],
  },
  {
    code: "orders.history.read",
    name: "Просмотр истории заказов",
    description: "Просмотр истории изменений заказов",
    category: "orders",
    implies: ["orders.read"],
  },
  {
    code: "order-statuses.read",
    name: "Просмотр статусов",
    description: "Просмотр конфигурации статусов заказов",
    category: "order-statuses",
  },
  {
    code: "order-statuses.write",
    name: "Управление статусами",
    description: "Создание и редактирование статусов заказов",
    category: "order-statuses",
    implies: ["order-statuses.read"],
  },

  // ==================== PAYMENTS ====================
  {
    code: "payments.read",
    name: "Просмотр платежей",
    description: "Просмотр списка платежей и их статусов",
    category: "payments",
  },
  {
    code: "payments.create",
    name: "Создание платежей",
    description: "Создание новых платежей, инвойсов, hold-операций",
    category: "payments",
    implies: ["payments.read"],
  },
  {
    code: "payments.update",
    name: "Редактирование платежей",
    description: "Изменение платежей",
    category: "payments",
    implies: ["payments.read"],
  },
  {
    code: "payments.delete",
    name: "Удаление платежей",
    description: "Удаление платежей / отвязка карт",
    category: "payments",
    implies: ["payments.read"],
  },
  {
    code: "payments.refund",
    name: "Возврат платежей",
    description: "Оформление возвратов по платежам",
    category: "payments",
    implies: ["payments.read", "orders.read"],
  },
  {
    code: "payments.cancel",
    name: "Отмена платежей",
    description: "Отмена платежей и hold-операций",
    category: "payments",
    implies: ["payments.read"],
  },
  {
    code: "payments.resume",
    name: "Возобновление платежей",
    description: "Возобновление приостановленных платежей / инвойсов",
    category: "payments",
    implies: ["payments.read"],
  },
  {
    code: "payments.sync",
    name: "Синхронизация платежей",
    description: "Принудительная синхронизация статусов платежей с провайдером",
    category: "payments",
    implies: ["payments.read"],
  },

  // ==================== AUDIT LOGS ====================
  {
    code: "audit-logs.read",
    name: "Просмотр аудит-логов",
    description: "Просмотр журнала действий администраторов",
    category: "audit-logs",
  },

  // ==================== TELEGRAM CLIENT ====================
  {
    code: "telegram-client.read",
    name: "Просмотр Telegram чатов",
    description: "Просмотр своих личных чатов через Telegram клиент",
    category: "telegram-client",
  },
  {
    code: "telegram-client.write",
    name: "Отправка сообщений",
    description: "Отправка сообщений в чатах через Telegram клиент",
    category: "telegram-client",
    implies: ["telegram-client.read"],
  },
  {
    code: "telegram-client.public",
    name: "Публичные сообщения",
    description: "Просмотр публичных сообщений всех админов",
    category: "telegram-client",
  },
  {
    code: "telegram-client.send",
    name: "Отправка через сессию",
    description: "Отправка сообщений / кодов через Telegram сессию",
    category: "telegram-client",
    implies: ["telegram-client.read"],
  },
  {
    code: "telegram-client.resend",
    name: "Повторная отправка",
    description: "Повторная отправка сообщений / кодов",
    category: "telegram-client",
    implies: ["telegram-client.read"],
  },
  {
    code: "telegram-client.cancel",
    name: "Отмена в Telegram",
    description: "Отмена операций / отправок в Telegram",
    category: "telegram-client",
    implies: ["telegram-client.read"],
  },
  {
    code: "telegram-client.verify",
    name: "Подтверждение в Telegram",
    description: "Подтверждение кодов и сессий Telegram",
    category: "telegram-client",
    implies: ["telegram-client.read"],
  },
  {
    code: "telegram.read",
    name: "Просмотр Telegram",
    description: "Чтение общих Telegram-ресурсов",
    category: "telegram",
  },
  {
    code: "telegram.write",
    name: "Управление Telegram",
    description: "Управление общими Telegram-ресурсами",
    category: "telegram",
    implies: ["telegram.read"],
  },
  {
    code: "telegram-channels.read",
    name: "Просмотр Telegram каналов",
    description: "Просмотр списка Telegram каналов и их настроек",
    category: "telegram-channels",
  },
  {
    code: "telegram-channels.write",
    name: "Управление Telegram каналами",
    description: "Создание, редактирование и удаление Telegram каналов",
    category: "telegram-channels",
    implies: ["telegram-channels.read"],
  },
  {
    code: "telegram-bots.read",
    name: "Просмотр Telegram ботов",
    description: "Просмотр списка Telegram ботов и их настроек",
    category: "telegram-bots",
  },
  {
    code: "telegram-bots.write",
    name: "Управление Telegram ботами",
    description: "Создание, редактирование и удаление Telegram ботов, управление webhook",
    category: "telegram-bots",
    implies: ["telegram-bots.read"],
  },

  // ==================== MODERATION ====================
  {
    code: "moderation.read",
    name: "Просмотр модерации",
    description: "Просмотр очереди модерации контента (посты, комментарии, отзывы)",
    category: "moderation",
  },
  {
    code: "moderation.write",
    name: "Модерация контента",
    description: "Одобрение и отклонение контента на модерации",
    category: "moderation",
    implies: ["moderation.read"],
  },
  {
    code: "reports.read",
    name: "Просмотр жалоб",
    description: "Просмотр жалоб пользователей на контент",
    category: "reports",
  },
  {
    code: "reports.write",
    name: "Обработка жалоб",
    description: "Рассмотрение и разрешение жалоб пользователей",
    category: "reports",
    implies: ["reports.read"],
  },

  // ==================== USER INVITATIONS ====================
  {
    code: "user-invitations.read",
    name: "Просмотр приглашений",
    description: "Просмотр списка приглашений пользователей",
    category: "user-invitations",
  },
  {
    code: "user-invitations.write",
    name: "Управление приглашениями",
    description: "Создание и редактирование приглашений",
    category: "user-invitations",
    implies: ["user-invitations.read"],
  },
  {
    code: "user-invitations.send",
    name: "Отправка приглашений",
    description: "Отправка приглашений пользователям через Telegram/Email",
    category: "user-invitations",
    implies: ["user-invitations.read"],
  },
  {
    code: "invitation-templates.read",
    name: "Просмотр шаблонов приглашений",
    description: "Просмотр списка шаблонов приглашений",
    category: "invitation-templates",
  },
  {
    code: "invitation-templates.write",
    name: "Управление шаблонами приглашений",
    description: "Создание и редактирование шаблонов приглашений",
    category: "invitation-templates",
    implies: ["invitation-templates.read"],
  },

  // ==================== CHAT (модерация чатов) ====================
  {
    code: "chat.read",
    name: "Просмотр чатов",
    description: "Просмотр сообщений в чатах",
    category: "chat",
  },
  {
    code: "chat.write",
    name: "Отправка в чаты",
    description: "Отправка сообщений в чаты",
    category: "chat",
    implies: ["chat.read"],
  },
  {
    code: "chat.warn",
    name: "Предупреждение в чате",
    description: "Выдача предупреждений участникам чата",
    category: "chat",
    implies: ["chat.read"],
  },
  {
    code: "chat.mute",
    name: "Мьют в чате",
    description: "Запрет писать в чат для пользователя",
    category: "chat",
    implies: ["chat.read"],
  },
  {
    code: "chat.unmute",
    name: "Снятие мьюта",
    description: "Снятие запрета писать в чат",
    category: "chat",
    implies: ["chat.read"],
  },
  {
    code: "chat.kick",
    name: "Кик из чата",
    description: "Исключение пользователя из чата",
    category: "chat",
    implies: ["chat.read"],
  },
  {
    code: "chat.ban",
    name: "Бан в чате",
    description: "Бан пользователя в чате",
    category: "chat",
    implies: ["chat.read"],
  },
  {
    code: "chat.analyze",
    name: "Анализ чатов",
    description: "Запуск анализа диалогов и переписки",
    category: "chat",
  },
  {
    code: "chat-moderation.read",
    name: "Просмотр чатов (модерация)",
    description: "Скрытый просмотр всех чатов для модерации",
    category: "chat-moderation",
  },
  {
    code: "chat-moderation.write",
    name: "Действия в чатах",
    description: "Удаление сообщений, mute, kick, ban пользователей в чатах",
    category: "chat-moderation",
    implies: ["chat-moderation.read"],
  },
  {
    code: "chat-analysis.read",
    name: "Просмотр анализа чатов",
    description: "Просмотр результатов анализа чатов",
    category: "chat-analysis",
  },
  {
    code: "chat-analysis.write",
    name: "Управление анализом чатов",
    description: "Запуск и настройка анализа чатов",
    category: "chat-analysis",
    implies: ["chat-analysis.read"],
  },

  // ==================== GAMES ====================
  {
    code: "games.read",
    name: "Просмотр игр",
    description: "Просмотр лобби и сессий игр",
    category: "games",
  },
  {
    code: "games.write",
    name: "Управление играми",
    description: "Создание и изменение игровых сессий",
    category: "games",
    implies: ["games.read"],
  },
  {
    code: "games.kick",
    name: "Кик из игр",
    description: "Исключение участника из игры",
    category: "games",
    implies: ["games.read"],
  },
  {
    code: "games.ban",
    name: "Бан в играх",
    description: "Бан игрока",
    category: "games",
    implies: ["games.read"],
  },
  {
    code: "games.cancel",
    name: "Отмена игр",
    description: "Отмена игровых сессий / лобби",
    category: "games",
    implies: ["games.read"],
  },
  {
    code: "game-moderation.read",
    name: "Просмотр игр (модерация)",
    description: "Скрытый просмотр лобби и игровых сессий",
    category: "game-moderation",
  },
  {
    code: "game-moderation.write",
    name: "Действия в играх",
    description: "Закрытие лобби, отмена сессий, kick, ban игроков",
    category: "game-moderation",
    implies: ["game-moderation.read"],
  },

  // ==================== VIOLATIONS ====================
  {
    code: "violations.read",
    name: "Просмотр нарушений",
    description: "Просмотр истории нарушений и наказаний пользователей",
    category: "violations",
  },
  {
    code: "violations.write",
    name: "Управление нарушениями",
    description: "Создание и отмена наказаний (warn, mute, ban)",
    category: "violations",
    implies: ["violations.read"],
  },
  {
    code: "violations.warn",
    name: "Предупреждение",
    description: "Выдача предупреждения пользователю",
    category: "violations",
    implies: ["violations.read"],
  },
  {
    code: "violations.mute",
    name: "Мьют пользователя",
    description: "Запрет писать для пользователя",
    category: "violations",
    implies: ["violations.read"],
  },
  {
    code: "violations.unmute",
    name: "Снятие мьюта",
    description: "Снятие запрета писать",
    category: "violations",
    implies: ["violations.read"],
  },
  {
    code: "violations.ban",
    name: "Бан пользователя",
    description: "Бан пользователя в системе",
    category: "violations",
    implies: ["violations.read"],
  },

  // ==================== CURRENCIES ====================
  {
    code: "currencies.read",
    name: "Просмотр валют",
    description: "Просмотр списка валют и курсов обмена",
    category: "currencies",
  },
  {
    code: "currencies.write",
    name: "Управление валютами",
    description: "Создание, редактирование валют и курсов обмена",
    category: "currencies",
    implies: ["currencies.read"],
  },

  // ==================== STAFF ANALYTICS ====================
  {
    code: "staff-analytics.read",
    name: "Просмотр аналитики сотрудников",
    description: "Просмотр статистики работы сотрудников",
    category: "staff-analytics",
  },
  {
    code: "staff-analytics.write",
    name: "Управление аналитикой сотрудников",
    description: "Настройка параметров аналитики сотрудников",
    category: "staff-analytics",
    implies: ["staff-analytics.read"],
  },
  {
    code: "staff-analytics.export",
    name: "Экспорт аналитики сотрудников",
    description: "Выгрузка отчётов аналитики сотрудников",
    category: "staff-analytics",
    implies: ["staff-analytics.read"],
  },

  // ==================== LIVESTREAMS ====================
  {
    code: "livestreams.read",
    name: "Просмотр трансляций",
    description: "Просмотр списка трансляций",
    category: "livestreams",
  },
  {
    code: "livestreams.create",
    name: "Создание трансляций",
    description: "Создание новых трансляций",
    category: "livestreams",
    implies: ["livestreams.read"],
  },
  {
    code: "livestreams.update",
    name: "Редактирование трансляций",
    description: "Изменение параметров трансляций",
    category: "livestreams",
    implies: ["livestreams.read"],
  },
  {
    code: "livestreams.delete",
    name: "Удаление трансляций",
    description: "Удаление трансляций",
    category: "livestreams",
    implies: ["livestreams.read"],
  },
  {
    code: "livestreams.publish",
    name: "Публикация трансляций",
    description: "Публикация трансляций",
    category: "livestreams",
    implies: ["livestreams.read", "livestreams.update"],
  },
  {
    code: "livestreams.unpublish",
    name: "Снятие с публикации",
    description: "Снятие трансляций с публикации",
    category: "livestreams",
    implies: ["livestreams.read", "livestreams.update"],
  },
  {
    code: "livestreams.cancel",
    name: "Отмена трансляций",
    description: "Отмена запланированных трансляций",
    category: "livestreams",
    implies: ["livestreams.read"],
  },
  {
    code: "livestreams.preview",
    name: "Предпросмотр трансляций",
    description: "Предпросмотр трансляции",
    category: "livestreams",
    implies: ["livestreams.read"],
  },
  {
    code: "livestream.host",
    name: "Ведение эфира",
    description: "Создание трансляций и ведение эфира от своего имени",
    category: "livestreams",
  },
  {
    code: "livestream.manage",
    name: "Управление эфирами",
    description: "Редактирование и отмена трансляций других админов, управление аудиторией",
    category: "livestreams",
    implies: ["livestreams.read"],
  },
  {
    code: "livestream.analytics",
    name: "Аналитика эфиров",
    description: "Просмотр метрик, сессий просмотра и статистики трансляций",
    category: "livestreams",
  },
  {
    code: "breakout-rooms.read",
    name: "Просмотр breakout-залов",
    description: "Просмотр breakout-залов трансляции",
    category: "breakout-rooms",
  },
  {
    code: "breakout-rooms.write",
    name: "Управление breakout-залами",
    description: "Создание, редактирование и закрытие breakout-залов",
    category: "breakout-rooms",
    implies: ["breakout-rooms.read"],
  },
  {
    code: "breakout-rooms.preview",
    name: "Предпросмотр breakout-залов",
    description: "Dry-run и предпросмотр распределения по breakout-залам",
    category: "breakout-rooms",
    implies: ["breakout-rooms.read"],
  },

  // ==================== TAGS ====================
  {
    code: "tags.read",
    name: "Просмотр тегов",
    description: "Просмотр списка тегов и назначений",
    category: "tags",
  },
  {
    code: "tags.write",
    name: "Управление тегами",
    description: "Создание и редактирование тегов",
    category: "tags",
    implies: ["tags.read"],
  },
  {
    code: "tags.delete",
    name: "Удаление тегов",
    description: "Удаление тегов",
    category: "tags",
    implies: ["tags.read"],
  },
  {
    code: "tags.assign",
    name: "Назначение тегов",
    description: "Назначение тегов пользователям и сущностям",
    category: "tags",
    implies: ["tags.read"],
  },
  {
    code: "tags.archive",
    name: "Архивирование тегов",
    description: "Архивирование тегов",
    category: "tags",
    implies: ["tags.read"],
  },
  {
    code: "tags.restore",
    name: "Восстановление тегов",
    description: "Восстановление архивных тегов",
    category: "tags",
    implies: ["tags.read"],
  },

  // ==================== UTM / COUNTRIES ====================
  {
    code: "utm-sources.read",
    name: "Просмотр UTM источников",
    description: "Просмотр списка UTM источников и их настроек",
    category: "utm-sources",
  },
  {
    code: "utm-sources.write",
    name: "Управление UTM источниками",
    description: "Создание, редактирование и удаление UTM источников",
    category: "utm-sources",
    implies: ["utm-sources.read"],
  },
  {
    code: "countries.read",
    name: "Просмотр стран",
    description: "Просмотр справочника стран и связанных тегов",
    category: "countries",
  },
  {
    code: "countries.write",
    name: "Управление странами",
    description: "Создание, редактирование, архивирование стран и привязка тегов",
    category: "countries",
    implies: ["countries.read"],
  },

  // ==================== PROMO CODES ====================
  {
    code: "promo-codes.read",
    name: "Просмотр промокодов",
    description: "Просмотр списка промокодов и их параметров",
    category: "promo-codes",
  },
  {
    code: "promo-codes.create",
    name: "Создание промокодов",
    description: "Создание новых промокодов",
    category: "promo-codes",
    implies: ["promo-codes.read"],
  },
  {
    code: "promo-codes.update",
    name: "Редактирование промокодов",
    description: "Изменение промокодов",
    category: "promo-codes",
    implies: ["promo-codes.read"],
  },
  {
    code: "promo-codes.delete",
    name: "Удаление промокодов",
    description: "Удаление промокодов",
    category: "promo-codes",
    implies: ["promo-codes.read"],
  },

  // ==================== AUDIENCE ====================
  {
    code: "audience.read",
    name: "Просмотр аудитории",
    description: "Просмотр предпросмотра аудитории, получение userId по сегменту, просмотр сохранённых сегментов",
    category: "audience",
  },
  {
    code: "audience.write",
    name: "Управление аудиторией",
    description: "Создание, редактирование и удаление сохранённых сегментов аудитории",
    category: "audience",
    implies: ["audience.read"],
  },
  {
    code: "audience.preview",
    name: "Предпросмотр аудитории",
    description: "Расчёт и предпросмотр размера сегмента",
    category: "audience",
    implies: ["audience.read"],
  },

  // ==================== TEMPLATES ====================
  {
    code: "email-templates.manage",
    name: "Управление шаблонами писем",
    description: "Просмотр, редактирование и предпросмотр email-шаблонов (OTP, приглашения, сброс пароля, уведомления)",
    category: "email-templates",
  },
  {
    code: "message-templates.read",
    name: "Просмотр шаблонов сообщений",
    description: "Просмотр унифицированных шаблонов сообщений (email/telegram)",
    category: "message-templates",
  },
  {
    code: "message-templates.write",
    name: "Управление шаблонами сообщений",
    description: "Создание и редактирование унифицированных шаблонов сообщений",
    category: "message-templates",
    implies: ["message-templates.read"],
  },
  {
    code: "message-templates.preview",
    name: "Предпросмотр шаблонов",
    description: "Предпросмотр рендера шаблона",
    category: "message-templates",
    implies: ["message-templates.read"],
  },

  // ==================== SOCIAL ====================
  {
    code: "social-feed.read",
    name: "Просмотр ленты",
    description: "Просмотр социальной ленты",
    category: "social",
  },
  {
    code: "social-posts.read",
    name: "Просмотр постов",
    description: "Просмотр постов социальной сети",
    category: "social",
  },
  {
    code: "social-posts.write",
    name: "Управление постами",
    description: "Создание и редактирование постов",
    category: "social",
    implies: ["social-posts.read"],
  },
  {
    code: "social-posts.publish",
    name: "Публикация постов",
    description: "Публикация и снятие с публикации постов",
    category: "social",
    implies: ["social-posts.read", "social-posts.write"],
  },
  {
    code: "social-comments.read",
    name: "Просмотр комментариев",
    description: "Просмотр комментариев к постам",
    category: "social",
  },
  {
    code: "social-comments.write",
    name: "Управление комментариями",
    description: "Создание и удаление комментариев",
    category: "social",
    implies: ["social-comments.read"],
  },
  {
    code: "social-lesson-comments.read",
    name: "Просмотр комментариев к урокам",
    description: "Просмотр комментариев к урокам курсов",
    category: "social",
  },
  {
    code: "social-lesson-comments.write",
    name: "Управление комментариями к урокам",
    description: "Создание и удаление комментариев к урокам",
    category: "social",
    implies: ["social-lesson-comments.read"],
  },
  {
    code: "social-likes.write",
    name: "Лайки",
    description: "Постановка и снятие лайков",
    category: "social",
  },
  {
    code: "social-follows.read",
    name: "Просмотр подписок",
    description: "Просмотр подписок и подписчиков",
    category: "social",
  },
  {
    code: "social-follows.write",
    name: "Управление подписками",
    description: "Подписка и отписка от пользователей",
    category: "social",
    implies: ["social-follows.read"],
  },
  {
    code: "social-profiles.read",
    name: "Просмотр профилей",
    description: "Просмотр публичных социальных профилей",
    category: "social",
  },
  {
    code: "social-profiles.write",
    name: "Управление профилем",
    description: "Изменение собственного социального профиля",
    category: "social",
    implies: ["social-profiles.read"],
  },
  {
    code: "social-reports.read",
    name: "Просмотр соцжалоб",
    description: "Просмотр жалоб в социальной сети",
    category: "social",
  },
  {
    code: "social-reports.write",
    name: "Обработка соцжалоб",
    description: "Создание жалоб в социальной сети",
    category: "social",
    implies: ["social-reports.read"],
  },
  {
    code: "social-moderation.read",
    name: "Просмотр модерации соцсети",
    description: "Просмотр очереди модерации социальной сети",
    category: "social-moderation",
  },
  {
    code: "social-moderation.approve",
    name: "Одобрение в соцсети",
    description: "Одобрение постов / комментариев на модерации",
    category: "social-moderation",
    implies: ["social-moderation.read"],
  },
  {
    code: "social-moderation.reject",
    name: "Отклонение в соцсети",
    description: "Отклонение постов / комментариев на модерации",
    category: "social-moderation",
    implies: ["social-moderation.read"],
  },
  {
    code: "social-admin.read",
    name: "Админ соцсети (просмотр)",
    description: "Админский просмотр социальной сети",
    category: "social",
  },
  {
    code: "social-admin.write",
    name: "Админ соцсети (управление)",
    description: "Админские действия в социальной сети",
    category: "social",
    implies: ["social-admin.read"],
  },

  // ==================== CALLS ====================
  {
    code: "calls.read",
    name: "Просмотр звонков",
    description: "Просмотр истории и состояний звонков",
    category: "calls",
  },
  {
    code: "calls.write",
    name: "Управление звонками",
    description: "Инициирование и управление звонками",
    category: "calls",
    implies: ["calls.read"],
  },

  // ==================== FILES ====================
  {
    code: "files.read",
    name: "Просмотр файлов",
    description: "Скачивание и просмотр файлов",
    category: "files",
  },
  {
    code: "files.write",
    name: "Управление файлами",
    description: "Загрузка и удаление файлов",
    category: "files",
    implies: ["files.read"],
  },

  // ==================== LEGAL ====================
  {
    code: "legal-pages.read",
    name: "Просмотр юр. страниц",
    description: "Просмотр юридических страниц (оферта, политика и т.п.)",
    category: "legal",
  },
  {
    code: "legal-pages.write",
    name: "Управление юр. страницами",
    description: "Создание и редактирование юридических страниц",
    category: "legal",
    implies: ["legal-pages.read"],
  },

  // ==================== LOCALIZATION ====================
  {
    code: "localization.read",
    name: "Просмотр локализации",
    description: "Просмотр языков и переводов",
    category: "localization",
  },
  {
    code: "localization.write",
    name: "Управление локализацией",
    description: "Управление языками и переводами",
    category: "localization",
    implies: ["localization.read"],
  },
  {
    code: "localization.export",
    name: "Экспорт локализации",
    description: "Выгрузка переводов",
    category: "localization",
    implies: ["localization.read"],
  },
  {
    code: "localization.sync",
    name: "Синхронизация локализации",
    description: "Импорт и синхронизация переводов",
    category: "localization",
    implies: ["localization.read", "localization.write"],
  },

  // ==================== MATRIX / E2EE ====================
  {
    code: "matrix-keys.write",
    name: "Управление ключами Matrix",
    description: "Управление identity/one-time ключами Matrix",
    category: "matrix",
  },
  {
    code: "matrix-room-keys.read",
    name: "Просмотр ключей комнат",
    description: "Чтение Matrix room keys",
    category: "matrix",
  },
  {
    code: "matrix-room-keys.write",
    name: "Управление ключами комнат",
    description: "Запись Matrix room keys",
    category: "matrix",
    implies: ["matrix-room-keys.read"],
  },
  {
    code: "matrix-to-device.write",
    name: "Matrix to-device сообщения",
    description: "Отправка Matrix to-device сообщений",
    category: "matrix",
  },

  // ==================== INTEGRATIONS ====================
  {
    code: "integrations.read",
    name: "Просмотр интеграций",
    description: "Просмотр интеграций и webhook",
    category: "integrations",
  },
  {
    code: "integrations.write",
    name: "Управление интеграциями",
    description: "Управление интеграциями и webhook",
    category: "integrations",
    implies: ["integrations.read"],
  },

  // ==================== PRESENCE / DASHBOARD / SEARCH ====================
  {
    code: "presence.read",
    name: "Просмотр присутствия",
    description: "Просмотр онлайн-статусов",
    category: "presence",
  },
  {
    code: "presence.write",
    name: "Управление присутствием",
    description: "Обновление собственного онлайн-статуса",
    category: "presence",
  },
  {
    code: "dashboard.read",
    name: "Просмотр дашборда",
    description: "Доступ к дашборду пользователя",
    category: "dashboard",
  },
  {
    code: "search.read",
    name: "Глобальный поиск",
    description: "Поиск по сущностям платформы",
    category: "search",
  },
  {
    code: "error-reports.write",
    name: "Отправка отчётов об ошибках",
    description: "Отправка client-side отчётов об ошибках",
    category: "error-reports",
  },
  {
    code: "telemetry.read",
    name: "Просмотр телеметрии",
    description: "Просмотр данных телеметрии",
    category: "telemetry",
  },
  {
    code: "telemetry.write",
    name: "Отправка телеметрии",
    description: "Отправка данных телеметрии",
    category: "telemetry",
  },
  {
    code: "tracks.write",
    name: "Запись треков",
    description: "Запись событий трекинга",
    category: "tracks",
  },

  // ==================== CLUB / DAILY PLANNER ====================
  {
    code: "club.read",
    name: "Просмотр клуба",
    description: "Просмотр контента клуба",
    category: "club",
  },
  {
    code: "club.write",
    name: "Управление клубом",
    description: "Управление контентом клуба",
    category: "club",
    implies: ["club.read"],
  },
  {
    code: "daily-planner.read",
    name: "Просмотр планировщика",
    description: "Просмотр ежедневного планировщика",
    category: "daily-planner",
  },
  {
    code: "daily-planner.write",
    name: "Управление планировщиком",
    description: "Создание и редактирование записей планировщика",
    category: "daily-planner",
    implies: ["daily-planner.read"],
  },
  {
    code: "daily-planner.publish",
    name: "Публикация планировщика",
    description: "Публикация записей планировщика",
    category: "daily-planner",
    implies: ["daily-planner.read", "daily-planner.write"],
  },

  // ==================== RECOMMENDATIONS / REPORTING ====================
  {
    code: "recommendations.read",
    name: "Просмотр рекомендаций",
    description: "Просмотр рекомендаций контента",
    category: "recommendations",
  },
  {
    code: "recommendations.write",
    name: "Управление рекомендациями",
    description: "Настройка рекомендаций",
    category: "recommendations",
    implies: ["recommendations.read"],
  },
  {
    code: "reporting.read",
    name: "Просмотр отчётности",
    description: "Просмотр отчётов",
    category: "reporting",
  },
  {
    code: "reporting.write",
    name: "Управление отчётностью",
    description: "Создание и настройка отчётов",
    category: "reporting",
    implies: ["reporting.read"],
  },
];
